import React, { FC } from "react";
import classnames from "classnames";
import { LEADERBOARD_BOARD } from "@shared/models";

// Relative, not the "@shared/components" alias: jest resolves no path aliases (no `jest` key in
// package.json), and this component's own test (LeaderboardBoardToggle.test.tsx) does not mock
// "@shared/components" — same recipe as InitialsAvatar's identical import at this same folder depth
// (CollaboratorCard.tsx, RankRow.tsx, PodiumTile.tsx).
import { PillToggle } from "../../../../../shared/components/PillToggle";

interface LeaderboardBoardToggleProps {
  board: LEADERBOARD_BOARD;
  onChange: (board: LEADERBOARD_BOARD) => void;
  // overview-leaderboard-card-plan.md Part 2: an optional modifier class, so the Overview rail card
  // can restyle this same presentational component for its own 376px width (CardHead's own
  // `className` prop precedent) rather than the card building a second, near-identical toggle.
  className?: string;
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L1: the Leaderboard TAB renames these to "Brightest
  // Sparks"/"Spark Spreaders" while the Overview rail card keeps its own "Received"/"Given" pair
  // (Figma `Leaderboard`, 9119:1089) -- an optional override, same "restyle without rebuilding"
  // precedent as `className` above, rather than a second toggle component owning the tab's own copy.
  labels?: Partial<Record<LEADERBOARD_BOARD.RECEIVED | LEADERBOARD_BOARD.GIVEN, string>>;
}

const DEFAULT_BOARD_LABELS: Record<LEADERBOARD_BOARD.RECEIVED | LEADERBOARD_BOARD.GIVEN, string> = {
  [LEADERBOARD_BOARD.RECEIVED]: "Received",
  [LEADERBOARD_BOARD.GIVEN]: "Given",
};

const BOARD_VALUES: (LEADERBOARD_BOARD.RECEIVED | LEADERBOARD_BOARD.GIVEN)[] = [
  LEADERBOARD_BOARD.RECEIVED,
  LEADERBOARD_BOARD.GIVEN,
];

/**
 * Presentational only — no dispatch/select of its own; `LeaderboardsContainer` owns all dispatching
 * (D5/D7's "conventions the build must follow"). Never renders `LEADERBOARD_BOARD.CROWNS` as an
 * option — crowns is its own always-visible row, not a board a viewer toggles into.
 *
 * PR2 (review-tab-redesign-plan.md): wraps the shared `PillToggle` rather than owning its own pill
 * markup/CSS — `PillToggle`'s own base styling (`pill-toggle`/`pill-toggle-option`) IS this component's
 * former `index.scss`, moved there verbatim as the shared control's base treatment. `className`/
 * `optionClassName` still forward this component's own `leaderboard-board-toggle`/
 * `leaderboard-board-toggle-option` class names, since LeaderboardsContainer/index.scss and
 * OverviewLeaderboard/index.scss both select on them directly for their own per-screen overrides.
 */
const LeaderboardBoardToggle: FC<LeaderboardBoardToggleProps> = (props) => {
  const { board, onChange, className, labels } = props;

  const options = BOARD_VALUES.map((value) => ({
    value,
    label: labels?.[value] ?? DEFAULT_BOARD_LABELS[value],
  }));

  return (
    <PillToggle
      options={options}
      value={board}
      onChange={onChange}
      className={classnames("leaderboard-board-toggle", className)}
      optionClassName="leaderboard-board-toggle-option"
    />
  );
};

export default LeaderboardBoardToggle;
